import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { Row, Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { gql } from 'apollo-boost';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { activateAuthLayout } from '../../../../store/actions';
import { getPortraitUrl } from '../../../../helpers/common';
import ImageDropzone from '../../../../components/ImageDropzone';

const GET_USER = gql`
    query Users {
        Users {
            id
            email
            FirstName
            LastName
            AgentCode
            TeamId
            ShowOnProposal
            Role
            Description
            Portrait
        }
    }
`;

const GET_TEAMS = gql`
    query {
        Teams {
            TeamId
            Name
        }
    }
`;

const CREATE_USER = gql`
    mutation createUser($createUserInput: createUserInput) {
        createUser(input: $createUserInput) {
            status
            message
            user_id
        }
    }
`;

const UPDATE_USER = gql`
    mutation updateUser($updateUserInput: updateUserInput) {
        updateUser(input: $updateUserInput) {
            status
            message
        }
    }
`;

const UPLOAD_PORTRAIT = gql`
    mutation uploadPersonPortrait($userId: ID!, $file: Upload!) {
        uploadPersonPortrait(userId: $userId, file: $file)
    }
`;

const EditUser = ({ activateAuthLayout }) => {
    const { id } = useParams();
    const isCreate = !id;
    const history = useHistory();
    const [createUser] = useMutation(CREATE_USER);
    const [updateUser] = useMutation(UPDATE_USER);
    const [uploadPortrait] = useMutation(UPLOAD_PORTRAIT);
    const { data: usersData, loading: usersLoading } = useQuery(GET_USER, { skip: isCreate });
    const { data: teamsData } = useQuery(GET_TEAMS);
    const [portraitPreview, setPortraitPreview] = useState(null);
    const [pendingPortraitFile, setPendingPortraitFile] = useState(null);

    useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

    if (!isCreate && usersLoading) return <div className="content"><div className="container-fluid"><p>Loading...</p></div></div>;

    const userRecord = isCreate ? null : usersData?.Users?.find(u => String(u.id) === String(id));

    if (!isCreate && !userRecord) return <div className="content"><div className="container-fluid"><p>User not found.</p></div></div>;

    const teamOptions = teamsData?.Teams?.map(t => ({ value: t.TeamId, label: t.Name })) ?? [];

    const initialValues = isCreate ? {
        email: '',
        FirstName: '',
        LastName: '',
        AgentCode: '',
        TeamId: null,
        ShowOnProposal: false,
        Role: '',
        Description: '',
        Portrait: null,
    } : {
        email: userRecord.email ?? '',
        FirstName: userRecord.FirstName ?? '',
        LastName: userRecord.LastName ?? '',
        AgentCode: userRecord.AgentCode ?? '',
        TeamId: userRecord.TeamId ?? null,
        ShowOnProposal: userRecord.ShowOnProposal ?? false,
        Role: userRecord.Role ?? '',
        Description: userRecord.Description ?? '',
        Portrait: userRecord.Portrait ?? null,
    };

    const handlePortraitDrop = (file, userId, setFieldValue) => {
        uploadPortrait({ variables: { userId, file } }).then(({ data }) => {
            const filename = data.uploadPersonPortrait;
            setFieldValue('Portrait', filename);
            setPortraitPreview(Object.assign(file, { preview: getPortraitUrl(filename) }));
            if (isCreate) {
                updateUser({ variables: { updateUserInput: { user_id: parseInt(userId), Portrait: filename } } });
            }
        });
    };

    const onSubmit = (values, { setSubmitting }) => {
        setSubmitting(true);
        if (isCreate) {
            createUser({
                variables: {
                    createUserInput: {
                        email: values.email,
                        FirstName: values.FirstName,
                        LastName: values.LastName,
                        AgentCode: values.AgentCode || null,
                        TeamId: values.TeamId,
                        ShowOnProposal: values.ShowOnProposal,
                        Role: values.Role || null,
                        Description: values.Description || null,
                        Portrait: null,
                    }
                }
            }).then(({ data: { createUser: { status, message, user_id } } }) => {
                if (status === 200) {
                    if (pendingPortraitFile && user_id) {
                        handlePortraitDrop(pendingPortraitFile, user_id, () => {});
                    }
                    Swal.fire({ icon: 'success', text: message, timer: 2000 });
                    history.push('/admin/manage-users');
                } else {
                    Swal.fire({ icon: 'error', text: message });
                }
            }).catch(err => console.log(err))
              .finally(() => setSubmitting(false));
        } else {
            updateUser({
                variables: {
                    updateUserInput: {
                        user_id: parseInt(id),
                        email: values.email,
                        FirstName: values.FirstName,
                        LastName: values.LastName,
                        AgentCode: values.AgentCode || null,
                        TeamId: values.TeamId,
                        ShowOnProposal: values.ShowOnProposal,
                        Role: values.Role || null,
                        Description: values.Description || null,
                        Portrait: values.Portrait,
                    }
                }
            }).then(({ data: { updateUser: { status, message } } }) => {
                Swal.fire({ icon: status === 200 ? 'success' : 'error', text: message, timer: 2000 });
                if (status === 200) history.push('/admin/manage-users');
            }).catch(err => console.log(err))
              .finally(() => setSubmitting(false));
        }
    };

    return (
        <div className="content">
            <div className="container-fluid">
                <div className="page-title-box">
                    <Row className="align-items-center">
                        <Col sm="12">
                            <h4 className="page-title">{isCreate ? 'Create Person' : 'Edit Person'}</h4>
                        </Col>
                    </Row>
                </div>
                <Formik
                    initialValues={initialValues}
                    validate={values => {
                        const errors = {};
                        if (!values.email) {
                            errors.email = 'Email is required';
                        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
                            errors.email = 'Invalid email';
                        }
                        if (!values.FirstName) errors.FirstName = 'First name is required';
                        if (!values.LastName) errors.LastName = 'Last name is required';
                        return errors;
                    }}
                    onSubmit={onSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Col lg="12">
                                    <Card>
                                        <CardHeader>Person Details</CardHeader>
                                        <CardBody>
                                            <FormGroup row>
                                                <Label sm={4}>Email <small className="text-danger">*</small></Label>
                                                <Col sm={8}>
                                                    <Input
                                                        type="text"
                                                        name="email"
                                                        value={values.email}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        invalid={Boolean(touched.email && errors.email)}
                                                    />
                                                    {touched.email && errors.email && <small className="text-danger">{errors.email}</small>}
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Label sm={4}>First Name <small className="text-danger">*</small></Label>
                                                <Col sm={8}>
                                                    <Input
                                                        type="text"
                                                        name="FirstName"
                                                        value={values.FirstName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        invalid={Boolean(touched.FirstName && errors.FirstName)}
                                                    />
                                                    {touched.FirstName && errors.FirstName && <small className="text-danger">{errors.FirstName}</small>}
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Label sm={4}>Last Name <small className="text-danger">*</small></Label>
                                                <Col sm={8}>
                                                    <Input
                                                        type="text"
                                                        name="LastName"
                                                        value={values.LastName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        invalid={Boolean(touched.LastName && errors.LastName)}
                                                    />
                                                    {touched.LastName && errors.LastName && <small className="text-danger">{errors.LastName}</small>}
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Label sm={4}>Agent ID</Label>
                                                <Col sm={8}>
                                                    <Input
                                                        type="text"
                                                        name="AgentCode"
                                                        value={values.AgentCode}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    />
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Label sm={4}>Team</Label>
                                                <Col sm={8}>
                                                    <Select
                                                        options={teamOptions}
                                                        isClearable
                                                        placeholder="Select team..."
                                                        value={teamOptions.find(o => o.value === values.TeamId) ?? null}
                                                        onChange={opt => setFieldValue('TeamId', opt?.value ?? null)}
                                                    />
                                                </Col>
                                            </FormGroup>
                                            {values.TeamId && (
                                                <>
                                                    <FormGroup row>
                                                        <Col sm={{ size: 8, offset: 4 }} className="d-flex align-items-center">
                                                            <input
                                                                type="checkbox"
                                                                name="ShowOnProposal"
                                                                id="ShowOnProposal"
                                                                checked={values.ShowOnProposal}
                                                                onChange={e => setFieldValue('ShowOnProposal', e.target.checked)}
                                                            />
                                                            <Label for="ShowOnProposal" className="ml-2 mb-0">Show on proposals</Label>
                                                        </Col>
                                                    </FormGroup>
                                                    {values.ShowOnProposal && (
                                                        <>
                                                            <FormGroup row>
                                                                <Label sm={4}>Portrait</Label>
                                                                <Col sm={8}>
                                                                    <ImageDropzone
                                                                        onDrop={([file]) => {
                                                                            if (!file) return;
                                                                            if (isCreate) {
                                                                                setPendingPortraitFile(file);
                                                                                setPortraitPreview(Object.assign(file, { preview: URL.createObjectURL(file) }));
                                                                                return;
                                                                            }
                                                                            handlePortraitDrop(file, id, setFieldValue);
                                                                        }}
                                                                        imageUrl={portraitPreview?.preview ?? (values.Portrait ? getPortraitUrl(values.Portrait) : null)}
                                                                        name="portrait image"
                                                                    />
                                                                </Col>
                                                            </FormGroup>
                                                            <FormGroup row>
                                                                <Label sm={4}>Role</Label>
                                                                <Col sm={8}>
                                                                    <Input
                                                                        type="text"
                                                                        name="Role"
                                                                        value={values.Role}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                </Col>
                                                            </FormGroup>
                                                            <FormGroup row>
                                                                <Label sm={4}>Description</Label>
                                                                <Col sm={8}>
                                                                    <Input
                                                                        type="textarea"
                                                                        name="Description"
                                                                        rows={4}
                                                                        value={values.Description}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                </Col>
                                                            </FormGroup>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </CardBody>
                                    </Card>
                                    <div className="text-lg-right">
                                        <Button color="primary" type="submit" disabled={isSubmitting}>Save User</Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

const mapStateToProps = ({ User: { user } }) => ({ user });

export default connect(mapStateToProps, { activateAuthLayout })(EditUser);
